import { useTranslation } from 'next-i18next';
import { useLocale } from '@/hooks';
import { getSubjectLocaleName } from '@/shared/category/getSubjectLocaleName';
import { PublisherLabelMap } from '@/shared/category/util';
import { Chip, SchoolInfoChip } from '@/shared/components/Chip/QuizInfoChip';
import { cn } from '@/utils/tailwind.util';
import type { FolderChip } from '../types/folder.types';
export type FolderChipsSize = 'card' | 'hero';

/**
 * ZUS 는 560px 이하에서 타이포 클래스를 한 단계 줄이므로 폰트는 분기하지 않는다.
 * 패딩은 자동축소 대상이 아니라 직접 분기한다.
 *
 * 축소 시점은 자리마다 다르다 — 카드는 `tablet-lg`, 히어로는 `tablet-sm`.
 */
const CHIP_TEXT_BY_SIZE: Record<FolderChipsSize, string> = {
  card: 'text-body-sm-semibold max-tablet-lg:px-2xs',
  hero: 'text-body-sm-semibold max-tablet-sm:px-2xs'
};
interface FolderCategoryChipsProps {
  chips: FolderChip[];
  size?: FolderChipsSize;
  emptyPlaceholderClassName?: string;
  className?: string;
}
export const FolderCategoryChips = ({
  chips,
  size = 'card',
  emptyPlaceholderClassName,
  className
}: FolderCategoryChipsProps) => {
  const chipText = CHIP_TEXT_BY_SIZE[size];
  const {
    locale
  } = useLocale();
  if (chips.length === 0) {
    if (!emptyPlaceholderClassName) return null;
    return <div aria-hidden className={cn('flex flex-wrap gap-3xs', emptyPlaceholderClassName)}>
        <Chip className={cn(chipText, 'invisible')}>{'\u00A0'}</Chip>
      </div>;
  }
  return <div className={cn('flex flex-wrap gap-3xs', className)} data-sentry-component="FolderCategoryChips" data-sentry-source-file="FolderCategoryChips.tsx">
      {chips.map(chip => {
      if (chip.kind === 'SCHOOL') {
        return <SchoolInfoChip key={chip.kind} className={chipText} schoolInfo={{
          schoolType: chip.schoolType ?? '',
          schoolGrade: chip.schoolGrade ?? '',
          schoolSemester: chip.schoolSemester
        }} otherCount={chip.otherCount} />;
      }
      const label = chip.kind === 'SUBJECT' ? getSubjectLocaleName({
        defaultName: chip.name,
        localizeNames: chip.localizeNames,
        locale
      }) : PublisherLabelMap[chip.publisherId];
      if (!label) return null;
      return <Chip key={chip.kind} className={chipText}>
            <span>{label}</span>
            <OtherCount count={chip.otherCount} />
          </Chip>;
    })}
    </div>;
};
const OtherCount = ({
  count
}: {
  count: number;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return count > 0 ? <span className={'ml-4xs text-assistive'} data-sentry-component="OtherCount" data-sentry-source-file="FolderCategoryChips.tsx">
      {t('common:global.andMoreCount', {
      n: count
    })}
    </span> : null;
};